import { CaretDownOutlined, CaretRightOutlined, DesktopOutlined } from '@ant-design/icons';
import { Card, Empty, Space, Tag, Typography } from 'antd';
import { useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { wsUrl } from '../lib/config';
import { useWebSocket } from '../lib/ws';

const { Text } = Typography;

type AppEntry = {
  id: string;
  name: string;
  primary: boolean;
  dominant: boolean;
  slideNumber: number | null;
  totalSlides: number | null;
};

type IncomingMessage =
  | { type: 'app-status'; connected: boolean }
  | { type: 'app-list'; apps: AppEntry[]; conflict: boolean }
  | { type: 'state'; enabled: boolean }
  | { type: 'notes'; text: string; slideNumber: number; totalSlides: number };

/**
 * One row in the monitor — subscribes to one slide license's room as admin.
 * Shows which machine is the current notes source, the slide number, and the
 * notes themselves (collapsible). Tech staff use this to follow along with
 * the speaker without needing to be near a slides machine.
 */
function SlideLicenseRow({
  licenseName,
  licenseKey,
  speakerLabel,
  initiallyOpen,
}: {
  licenseName: string;
  licenseKey: string;
  speakerLabel: string;
  initiallyOpen: boolean;
}) {
  const { session } = useAuth();
  const [open, setOpen] = useState(initiallyOpen);
  const [notes, setNotes] = useState<string>('');
  const [slide, setSlide] = useState<{ n: number; t: number } | null>(null);
  const [apps, setApps] = useState<AppEntry[]>([]);
  const [legacyConnected, setLegacyConnected] = useState(false);

  const url = useMemo(() => {
    if (!session) return null;
    return wsUrl(
      `/api/slides/${encodeURIComponent(licenseKey)}?role=admin&token=${encodeURIComponent(session.token)}`,
    );
  }, [licenseKey, session]);

  useWebSocket<IncomingMessage>(url, (msg) => {
    if (msg.type === 'notes') {
      setNotes((msg.text ?? '').replace(/\r/g, '\n'));
      setSlide({ n: msg.slideNumber, t: msg.totalSlides });
    } else if (msg.type === 'app-list') {
      setApps(msg.apps);
    } else if (msg.type === 'app-status') {
      // Fallback for pre-rebuild Workers that don't emit app-list yet.
      setLegacyConnected(msg.connected);
    }
  });

  const dominant = apps.find((a) => a.dominant);
  const anyConnected = apps.length > 0 || legacyConnected;
  const hasHtml = /<\/?[a-z][\s\S]*>/i.test(notes);

  return (
    <div style={{ borderTop: '1px solid #f0f0f0', padding: '12px 4px' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <CaretDownOutlined /> : <CaretRightOutlined />}
        <DesktopOutlined style={{ color: '#428e3e' }} />
        <Text strong>{licenseName}</Text>
        <Text type="secondary">— {speakerLabel}</Text>
        <span style={{ flex: 1 }} />
        {slide && (
          <Tag color="blue">
            Slide {slide.n} / {slide.t}
          </Tag>
        )}
        {!anyConnected && <Tag>No machine connected</Tag>}
        {anyConnected && dominant && (
          <Tag color="green">{dominant.name}</Tag>
        )}
        {anyConnected && !dominant && apps.length === 0 && (
          <Tag color="green">Connected</Tag>
        )}
      </div>
      {open && (
        <div style={{ marginTop: 10, marginLeft: 24 }}>
          {apps.length > 1 && (
            <Space size={4} wrap style={{ marginBottom: 8 }}>
              {apps.map((a) => (
                <Tag key={a.id} color={a.dominant ? 'green' : a.primary ? 'blue' : 'default'}>
                  {a.name}
                  {a.dominant ? ' · notes' : a.primary ? ' · primary' : ' · backup'}
                  {a.slideNumber != null && ` · ${a.slideNumber}/${a.totalSlides}`}
                </Tag>
              ))}
            </Space>
          )}
          <div
            style={{
              background: hasHtml ? '#fff' : '#1f1f1f',
              color: hasHtml ? '#222' : '#fff',
              padding: '12px 14px',
              borderRadius: 6,
              border: hasHtml ? '1px solid #d9d9d9' : 'none',
              minHeight: 60,
              maxHeight: 240,
              overflowY: 'auto',
              fontSize: 14,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {notes ? (
              <div dangerouslySetInnerHTML={{ __html: notes }} />
            ) : (
              <Text type="secondary" italic style={{ color: hasHtml ? undefined : '#bbb' }}>
                {anyConnected ? 'No notes for the current slide.' : 'Waiting for slides machine…'}
              </Text>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export type MonitoredLicense = {
  licenseId: string;
  licenseName: string;
  licenseKey: string;
  speakerLabel: string;
};

export function SlidesMonitor({ licenses }: { licenses: MonitoredLicense[] }) {
  const [collapsed, setCollapsed] = useState(licenses.length > 3);
  if (licenses.length === 0) return null;
  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <div
          style={{ cursor: 'pointer', userSelect: 'none' }}
          onClick={() => setCollapsed((v) => !v)}
        >
          {collapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}{' '}
          Slides monitor{' '}
          <Text type="secondary" style={{ fontWeight: 'normal' }}>
            ({licenses.length} {licenses.length === 1 ? 'deck' : 'decks'})
          </Text>
        </div>
      }
      styles={{ body: { padding: collapsed ? 0 : '0 16px 8px' } }}
    >
      {!collapsed &&
        (licenses.length === 0 ? (
          <Empty description="No active slide licenses for this event." />
        ) : (
          licenses.map((l, i) => (
            <SlideLicenseRow
              key={l.licenseId}
              licenseName={l.licenseName}
              licenseKey={l.licenseKey}
              speakerLabel={l.speakerLabel}
              initiallyOpen={i === 0 && licenses.length <= 2}
            />
          ))
        ))}
    </Card>
  );
}
